import type { FC } from 'react';
import type { AirportWithActive } from '@/types/domain/core';
import { Divider, Flex } from '@ui';
import { AnimatePresence, motion } from 'motion/react';
import { useCallback, useRef, useState } from 'react';
import { AirportsListItem } from '@/components/AirportsList/components/AirportsListItem/AirportsListItem';
import styles from './AirportsList.module.css';

type Props = {
    airports: AirportWithActive[];
    value: number;
    onChange: (value: number) => void;
};

export const AirportsList: FC<Props> = ({ airports, value, onChange }) => {
    const [firstAirport, ...restAirports] = airports;

    const containerRef = useRef<HTMLDivElement>(null);
    const [isAnimating, setIsAnimating] = useState(false);

    const [newPosition, setNewPosition] = useState({ top: 0, height: 0 });
    const [prevPosition, setPrevPosition] = useState({ top: 0, height: 0 });

    const handleChange = useCallback((newValue: number) => {
        if (newValue === value)
            return;

        onChange(newValue);

        if (!containerRef.current)
            return;

        const containerRect = containerRef.current.getBoundingClientRect();
        const selectedItem = containerRef.current.querySelector(`[data-airport-id="${newValue}"]`);
        if (!selectedItem)
            return;

        const itemRect = selectedItem.getBoundingClientRect();
        const newestPosition = {
            top: itemRect.top - containerRect.top,
            height: itemRect.height
        };

        setPrevPosition({ ...newPosition });
        setNewPosition(newestPosition);

        setIsAnimating(true);
    }, [onChange, newPosition, value]);

    return (
        <Flex vertical gap={16} flex="0 0 calc(20% - 40px)" className={styles.container} ref={containerRef}>
            <AnimatePresence>
                {isAnimating && (
                    <motion.div
                        key={`selection-${value}`}
                        className={styles.selectionBackground}
                        initial={{
                            top: prevPosition.top,
                            height: prevPosition.height
                        }}
                        animate={{
                            top: newPosition.top,
                            height: newPosition.height
                        }}
                        transition={{
                            type: 'spring',
                            stiffness: 300,
                            damping: 30,
                            duration: 0.5
                        }}
                        onAnimationComplete={() => setIsAnimating(false)}
                    />
                )}
            </AnimatePresence>

            <div className={styles.firstAirport}>
                <AirportsListItem
                    airport={firstAirport}
                    selected={value === firstAirport.id}
                    onSelect={() => handleChange(firstAirport.id)}
                    airportId={firstAirport.id}
                />
            </div>
            <Flex justify="center">
                <Divider width={48} />
            </Flex>
            <Flex vertical gap={2} className={styles.airports}>
                {restAirports.map(airport => (
                    <AirportsListItem
                        key={airport.id}
                        airport={airport}
                        selected={value === airport.id}
                        onSelect={() => handleChange(airport.id)}
                        airportId={airport.id}
                    />
                ))}
            </Flex>
            <div className={styles.peekShadow} />
        </Flex>
    );
};
